import { mentionsToPlainText } from './task-contexts';

/**
 * `#tags` in task text (title, description, chat). A tag starts after a space, the start of a
 * line or an opening bracket (so `page#section` in a link isn't one), is up to 40 letters,
 * digits, `-` or `_`, and has at least one letter (so `#123` and `#2` stay plain text).
 * Tags are compared and stored in lower case, without the `#`.
 */
const TAG_RE = /(^|[\s([{,;:!?"'])#([a-z0-9][a-z0-9_-]{0,39})(?![a-z0-9_-])/gi;

export const TASK_TAG_MAX = 40;

function clean(raw: string): string | null {
  const tag = raw.toLowerCase().replace(/[-_]+$/, '');
  return /[a-z]/.test(tag) ? tag : null;
}

/** Where the tags are in `text`, for highlighting. `start`/`end` include the `#`. */
export function tagRanges(text: string): Array<{ start: number; end: number; tag: string }> {
  const out: Array<{ start: number; end: number; tag: string }> = [];
  for (const match of text.matchAll(TAG_RE)) {
    const tag = clean(match[2]);
    if (!tag) continue;
    const start = (match.index ?? 0) + match[1].length;
    out.push({ start, end: start + 1 + match[2].replace(/[-_]+$/, '').length, tag });
  }
  return out;
}

/** The tags in one text, in order, without repeats. Mentions are ignored (`@[C#](…)` isn't a tag). */
export function parseTags(text: string | null | undefined): string[] {
  if (!text) return [];
  return [...new Set(tagRanges(mentionsToPlainText(text)).map((r) => r.tag))];
}

/** Every tag on a task: its title, description and chat messages together. */
export function taskTags(texts: Array<string | null | undefined>): string[] {
  return [...new Set(texts.flatMap(parseTags))];
}

/**
 * The `#query` being typed just before the caret, if any (for the tag picker). Null once
 * there's a space after it, or when the `#` is part of a word or link.
 */
export function activeTagQuery(text: string, caret: number): { query: string; start: number } | null {
  const match = /(^|[\s([{,;:!?"'])#([a-z0-9_-]{0,40})$/i.exec(text.slice(0, caret));
  if (!match) return null;
  return { query: match[2].toLowerCase(), start: caret - match[2].length - 1 };
}
